import { useGSAP } from '@gsap/react';
import { graphql, useStaticQuery } from 'gatsby';
import gsap from 'gsap';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLinkedin } from 'react-icons/fa';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { ALL_LANGS } from '../../../constants';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { ModuleBgColor } from '../../../templates/Page';
import { getUrlFromVersatileLink, localize } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import SmartLink from '../SmartLink';
import TextLink from '../TextLink';
import * as styles from './TeamModule.module.scss';

export const TeamModuleFragment = graphql`
  fragment TeamModule on SanityPageTeamModule {
    title {
      ...LocaleStringFragment
    }
    teamMembersToUse
    teamMembersReferences {
      ...Person
    }
  }
`;

export type Person = {
  name: string;
  position: string;
  text?: string;
  linkedinUrl?: string;
} & (
  | {
      isTeamMember: true;
      image: SanityImageType;
      position: string;
    }
  | {
      isTeamMember?: boolean;
      image?: SanityImageType;
      position?: string;
    }
);

export type TeamModuleProps = {
  title: string;
  className?: string;
} & (
  | {
      teamMembersToUse: 'all';
      teamMembersReferences?: never;
    }
  | {
      teamMembersToUse: 'chooseManually';
      teamMembersReferences: Array<Person>;
    }
);

interface QueryData {
  allSanityPerson: {
    nodes: Array<Person>;
  };
}

export function getModuleBgColor(props: TeamModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'white';
}

function TeamModule(props: TeamModuleProps & CommonModuleProps): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityPerson(sort: { orderRank: ASC }) {
        nodes {
          ...Person
        }
      }
    }
  `);
  const {
    title,
    teamMembersToUse,
    teamMembersReferences,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const { allSanityPerson } = localizedData;

  const containerRef = useRef<HTMLElement | null>(null);

  const [expandedTextIndices, setExpandedTextIndices] = useState<number[]>([]);

  const handleTextToggle = (index: number) => {
    setExpandedTextIndices(prevIndices =>
      prevIndices.includes(index) ? prevIndices.filter(i => i !== index) : [...prevIndices, index],
    );
  };

  const teamMembersToShow =
    teamMembersToUse === 'chooseManually' ? teamMembersReferences : allSanityPerson.nodes;

  useGSAP(
    () => {
      if (teamMembersToShow.length < 1) {
        return; // avoid errors on console
      }

      [styles.mobile, styles.desktop].forEach(mediaClass => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: `.${styles.teamMembersContainer}.${mediaClass}`,
            start: 'top 75%',
          } satisfies ScrollTrigger.Vars,
        });

        tl.from(
          mediaClass === styles.desktop
            ? `.${styles.teamMembersContainer}.${styles.desktop} > div > div > div`
            : `.${styles.mobile} .${styles.personContainer}`,
          {
            y: 200,
            duration: 0.8,
            stagger: mediaClass === styles.desktop ? 0 : 0.1,
          },
        );
      });
    },
    { scope: containerRef },
  );

  function renderTeamMembersContainer() {
    return teamMembersToShow
      .filter(teamMember => !!teamMember.isTeamMember && !!teamMember.image)
      .map((teamMember, i) => (
        <div className={styles.personContainer} key={i}>
          {teamMember.image && (
            <div className={clsx(styles.imageContainer, i % 2 === 0 && styles.evenContainer)}>
              <div className={styles.imageWrapper}>
                <Image image={teamMember.image} className={styles.image} />
              </div>
              {teamMember.linkedinUrl && (
                <SmartLink
                  to={getUrlFromVersatileLink({
                    linkType: 'externalLink',
                    externalLink: {
                      url: teamMember.linkedinUrl,
                    },
                  })}
                  className={styles.linkedinContainer}
                >
                  <div className={styles.visuallyHidden}>{teamMember.name} Linkedin</div>
                  <FaLinkedin />
                </SmartLink>
              )}
            </div>
          )}
          <h3 className={styles.name}>{teamMember.name}</h3>
          <p className={styles.position}>{teamMember.position}</p>
          {teamMember.text && (
            <>
              {expandedTextIndices.includes(i) && <p className={styles.text}>{teamMember.text}</p>}
              <TextLink
                styleOnly
                withExternalIcon={false}
                onClick={() => {
                  handleTextToggle(i);
                }}
                className={clsx(
                  styles.readMoreLink,
                  expandedTextIndices.includes(i) && styles.expandedText,
                )}
              >
                {expandedTextIndices.includes(i)
                  ? t('readLessButton.title', 'Ler Menos')
                  : t('readMoreButton.title', 'Ler Mais')}
                <div className={styles.iconContainer}>
                  <div className={styles.iconVerticalLine}></div>
                  <div className={styles.iconHorizontalLine}></div>
                </div>
              </TextLink>
            </>
          )}
        </div>
      ));
  }

  return teamMembersToShow.length > 0 ? (
    <ModuleLayout
      id={moduleId}
      className={clsx(className, styles.container)}
      contentClassName={clsx(styles.contentContainer)}
      title={title}
      moduleRef={containerRef}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={clsx(styles.teamMembersContainer, styles.mobile)}>
        {renderTeamMembersContainer()}
      </div>
      <div className={clsx(styles.teamMembersContainer, styles.desktop)}>
        <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 720: 2, 1200: 3 }}>
          <Masonry gutter="56px">{renderTeamMembersContainer()}</Masonry>
        </ResponsiveMasonry>
      </div>
    </ModuleLayout>
  ) : (
    <></>
  );
}

export default TeamModule;
